import { z } from "zod";
import { AutosaveTriggerName } from "./autosave.js";
import { CheckpointId, U64String } from "./ids.js";

/** Every SSE event in §20.3. The SSE `event:` field equals `type`. */
export const SseEvent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("checkpoint.received"), checkpointId: CheckpointId }),
  z.object({ type: z.literal("checkpoint.verified"), checkpointId: CheckpointId }),
  z.object({ type: z.literal("checkpoint.rejected"), checkpointId: CheckpointId, reason: z.string() }),
  z.object({ type: z.literal("chain.progress"), done: z.number().int(), total: z.number().int() }),
  z.object({ type: z.literal("chain.committed"), signature: z.string(), saveSeq: z.number().int() }),
  z.object({ type: z.literal("ownership.changed"), asset: z.string(), direction: z.enum(["in", "out"]) }),
  z.object({ type: z.literal("money.changed"), gameBalance: U64String, offset: z.string().regex(/^-?\d{1,20}$/), walletRcd: U64String }),
  z.object({ type: z.literal("rcd.claimed"), amount: U64String }),
  z.object({ type: z.literal("rcd.deposited"), amount: U64String }),
  // ₽ is waiting at the player's deposit address (§15.5.6); the next save credits it in game.
  z.object({ type: z.literal("rcd.deposit_pending"), amount: U64String }),
  // A burn or release refund paid as ₽ (§15.5.5); `lamports` went to the treasury.
  z.object({ type: z.literal("rcd.refunded"), asset: z.string(), amount: U64String, lamports: U64String }),
  // A deposit, claim, ₽ fee (relay, Day Care, levels) or ₽ refund changed game_balance: the open session writes `amount` into the purse (PURSE record, §14.6).
  z.object({
    type: z.literal("purse.credit"),
    creditId: z.string().uuid(),
    sessionId: z.string().uuid(),
    amount: z.number().int(),
    reason: z.enum(["deposit", "claim", "fee", "refund"]),
  }),
  z.object({ type: z.literal("session.superseded") }),
  z.object({ type: z.literal("taint.detected"), kind: z.string(), detail: z.string() }),
  z.object({ type: z.literal("migration.progress"), moved: z.number().int(), total: z.number().int() }),
  z.object({
    type: z.literal("vault.changed"),
    lamports: U64String,
    pendingMints: z.number().int(),
    pendingItemBag: z.boolean(),
    topUpNeeded: U64String,
    /** §15.5.5: in-game ₽ still needed for the waiting mints. */
    mintRcdNeeded: U64String.default("0"),
    /** Mints wait for the operator to refill the rent pool, not for the player. */
    rentPoolShort: z.boolean().default(false),
  }),
  z.object({ type: z.literal("vault.funded") }),
  z.object({ type: z.literal("mon.refunded"), asset: z.string(), lamports: U64String }),
  // §5.9: levels bought with ₽ (shown from the next load), and the Day Care surcharge for levels grown there.
  z.object({ type: z.literal("mon.levels_bought"), asset: z.string(), fromLevel: z.number().int(), toLevel: z.number().int(), rcd: U64String }),
  z.object({ type: z.literal("day_care.charged"), rcd: U64String }),
  z.object({ type: z.literal("autosave.fired"), triggers: z.array(AutosaveTriggerName), frame: U64String }),
  z.object({
    type: z.literal("items.changed"),
    index: z.number().int().min(0).max(9),
    inGame: z.number().int(),
    credit: z.number().int(),
    debt: z.number().int(),
  }),
  z.object({ type: z.literal("item.claimed"), index: z.number().int(), amount: z.number().int() }),
  z.object({ type: z.literal("item.deposited"), index: z.number().int(), amount: z.number().int() }),
  z.object({ type: z.literal("sync.pending_cleared"), saveSeq: z.number().int() }),
  // Published to both players once link.replay finishes. `winner` is a Player PDA, set only for decided battles.
  z.object({
    type: z.literal("link.result"),
    lobbyId: z.string().uuid(),
    linkSessionId: z.string().uuid(),
    activity: z.enum(["trade", "battle"]),
    outcome: z.enum(["completed", "won", "lost", "draw", "aborted", "desync"]),
    winner: z.string().optional(),
  }),
  z.object({
    type: z.literal("rcd.swap.updated"),
    swapId: z.string().uuid(),
    status: z.enum(["built", "submitted", "confirmed", "finalized", "failed", "expired"]),
    signature: z.string().optional(),
    reason: z.string().optional(),
  }),
  z.object({
    type: z.literal("market.listing.updated"),
    listingId: z.string().uuid(),
    status: z.enum(["open", "sold", "cancelled", "expired"]),
    asset: z.string(),
  }),
  z.object({
    type: z.literal("market.purchase.updated"),
    intentId: z.string().uuid(),
    listingId: z.string().uuid().optional(),
    status: z.enum(["created", "built", "submitted", "confirmed", "finalized", "failed", "expired"]),
    signature: z.string().optional(),
    reason: z.string().optional(),
  }),
  z.object({
    type: z.literal("shop.purchase.updated"),
    intentId: z.string().uuid(),
    status: z.enum(["created", "built", "submitted", "confirmed", "finalized", "failed", "expired"]),
    signature: z.string().optional(),
    reason: z.string().optional(),
  }),
]);
export type SseEvent = z.infer<typeof SseEvent>;
export type SseEventType = SseEvent["type"];

/** Postgres NOTIFY channel used to fan SSE events out to API instances. */
export const SSE_NOTIFY_CHANNEL = "rc_sse";
export interface SseNotifyPayload {
  playerId: string;
  eventId: string; // sse_events.id
}
